import { createAsyncStoragePersister } from '@tanstack/query-async-storage-persister';
import type { PersistQueryClientOptions } from '@tanstack/query-persist-client-core';

/**
 * Offline viewing of the client's report + journey (CP-P4-T02/DEC-0038).
 * Snapshots the already-fetched, already-authorized TanStack Query cache to
 * `localStorage` — NOT a service-worker network cache, since every API call
 * carries a live bearer token and is per-user/dynamic (the wrong shape for a
 * generic HTTP cache). `localStorage`'s synchronous methods satisfy the
 * `AsyncStorage` interface directly (its values are `T | Promise<T>`).
 *
 * Deliberately scoped to ONLY the report + journey queries — explicitly not
 * auth/me, questionnaires, or bookings, matching the task's literal "cache
 * the viewed report and journey."
 */
export const PERSISTED_CACHE_KEY = 'brainkey-client-portal-cache';

const persister = createAsyncStoragePersister({
  storage: window.localStorage,
  key: PERSISTED_CACHE_KEY,
});

const OFFLINE_CACHEABLE_SCOPES = new Set(['client-reports', 'client-journey']);

export const persistOptions: Omit<PersistQueryClientOptions, 'queryClient'> = {
  persister,
  // Don't resurrect week-old cached data indefinitely on a reload.
  maxAge: 24 * 60 * 60 * 1000,
  dehydrateOptions: {
    shouldDehydrateQuery: (query) => {
      const [scope] = query.queryKey as [string];
      return OFFLINE_CACHEABLE_SCOPES.has(scope);
    },
  },
};
